import { logError, logInfo } from '@/services/log';
import { ENVIRONMENT_CODE } from '@/utils/config';

export const CONTENTFUL_SLUG_PREFIX = 'cb';
export const LAMBDA_TOKEN = 'nRn43mTWwmeEmcjn4isnlsnc';

/*
  Shortcuts, that are "relative", will resolve to the current host name.
  (Lambda runtime sees the host name as something.execute-api.us-east-1.amazonaws.com)  
  Return the actual host name, based on the node environment setting: CLAYTON_ENVIRONMENT
  This can also be set in ".env.local"
*/
export const getEnvironmentHost = () => {
  const options = {
    dev: 'clayton-built.dev.cct-pubweb.com',
    itg: 'clayton-built.itg.cct-pubweb.com',
    qua: 'clayton-built.qua.cct-pubweb.com',
    prod: 'www.claytonbuilt.com',
    development: 'localhost:3000',
    localhost: 'localhost:3000',
  };
  return options[ENVIRONMENT_CODE!] || options['prod'];
};

/*
  Developers may provide a preference for the target ClaytonBuilt API server.
  This is a local property, on the developer's machine.
  This property doesn't exist when running as an AWS Lambda.
  Create/Edit the file ".env.local" in the project root.  For example:
  CLAYTON_BUILT_API="itg"

  NOTE: For localhost, the port number determined in "getContentApiUrl"
*/
const getPreferenceApiAddress = () => {
  const options = {
    dev: 'https://api.claytonhomes.dev.cct-pubweb.com/claytonbuilt/',
    itg: 'https://api.claytonhomes.itg.cct-pubweb.com/claytonbuilt/',
    qua: 'https://api.claytonhomes.qua.cct-pubweb.com/claytonbuilt/',
    prod: 'https://api.claytonhomes.com/claytonbuilt/',
    localhost: 'http://localhost/',
  };

  return options[ENVIRONMENT_CODE!] || options['prod'];
};

/*
  For local development, determine the API's address based on the developer's preference.
  Otherwise, use the translated Octo varible.
*/
const BASE_API_URL = getPreferenceApiAddress();

export const isLowerEnvironment = () => {
  const options = ['dev', 'itg', 'qua'];
  return options.includes(ENVIRONMENT_CODE!);
};

interface IGetContent {
  version?: string;
  port?: string;
  endpoint?: string;
  params?: { name: string; value: any }[];
  protocol?: string;
}

export const getContentApiUrl = ({
  version = 'v2',
  port,
  endpoint,
  params,
  protocol = 'https',
}: IGetContent) => {
  try {
    if (!Array.isArray(params)) {
      logError({
        context: 'getContentApiUrl',
        txt: 'params must be an array',
      });
      return null;
    }

    const url = `${getPreferenceApiAddress()}${version}/${endpoint ? endpoint + '/' : ''}`;
    const urlObj = new URL(url); //throws an error if poorly formatted.
    if (urlObj.host?.startsWith('localhost')) {
      urlObj.protocol = 'http';
      urlObj.port = port || '3001';
    } else {
      urlObj.protocol = protocol;
      if (port) {
        urlObj.port = port;
      }
    }

    let urlParameters: string[] = [];
    if (params) {
      for (const p of params) {
        urlParameters.push(`${p.name}=${p.value}`);
      }
    }

    const finalUrl = urlParameters[0]
      ? urlObj.toString() + '?' + urlParameters.join('&')
      : urlObj.toString();

    return finalUrl;
  } catch (err) {
    logInfo({
      context: 'getContentApiUrl',
      txt: `process.env.CLAYTON_ENVIRONMENT = ${process?.env?.CLAYTON_ENVIRONMENT}   ENVIRONMENT_CODE=${ENVIRONMENT_CODE}   BASE_API_URL=${BASE_API_URL}`,
      obj: err,
    });
    logError({
      context: 'getContentApiUrl',
      txt: 'url poorly formatted?',
      obj: err,
    });
    return null;
  }
};
